/*!
 * liveRoomPanel.js — Oscilla Live's room box: join a room, see who you are
 * © 2026 Rob Canning — GPLv3
 *
 *   live.html?room=jam                       the room filled in (on the landing too, liveLanding.js)
 *   live.html?room=jam&relay=wss://host/x    a relay elsewhere (default: the page's, below)
 *
 * The page says where its relay is (<meta name="oscilla-relay">): this-host,
 * its own /live-relay, as Oscilla's server has; a wss:// address; or none --
 * a static host with no relay (build-live.mjs, LIVE_RELAY) -- and then there
 * are no rooms: no box, no Room on the landing.
 *
 * The password is typed, never put in the address. It is kept for this tab
 * only (sessionStorage), so a reload in the middle of a set joins again
 * without asking; the name is kept in the browser (localStorage).
 */

import { joinRoom } from "../system/roomLink.js";

const NAME_KEY = "oscilla.liveName";
const store = (s, k, v) => { try { v === undefined ? s.removeItem(k) : s.setItem(k, v); } catch { /* private mode */ } };
const read = (s, k) => { try { return s.getItem(k); } catch { return null; } };

/** The relay this page joins rooms on, or null: none. */
export function relayAddress() {
  const asked = new URLSearchParams(location.search).get("relay");
  const page = document.querySelector('meta[name="oscilla-relay"]')?.content?.trim() || "this-host";
  const r = asked || page;
  if (r === "none") return null;
  if (r !== "this-host") return r;
  return `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/live-relay`;
}

/**
 * The box, in the corner of live.html (#live-room).
 * @param {{ onJoined?: (room: string) => void, onLeft?: () => void }} [o]
 * @returns {{ join(room, password, name), kept(room): string|null, ask(room), room: string, name: string }}
 *   kept: this tab's password for the room, from before a reload; ask: the box
 *   open on that room, waiting for its password
 */
export function initRoomPanel({ onJoined = () => {}, onLeft = () => {} } = {}) {
  const box = document.getElementById("live-room");
  if (!box) return null;
  const params = new URLSearchParams(location.search);
  const relay = relayAddress();
  if (!relay) {
    box.querySelector(".live-room-toggle")?.remove();
    box.querySelector(".live-room-body")?.remove();
    return null;
  }
  const form = box.querySelector("form");
  const status = box.querySelector(".live-room-status");
  const toggle = box.querySelector(".live-room-toggle");
  const leaveBtn = box.querySelector(".live-room-leave");
  const field = n => form.elements.namedItem(n);
  const passKey = room => `oscilla.livePass:${relay}:${room}`;
  let link = null;

  field("room").value = params.get("room") || "";
  field("name").value = read(localStorage, NAME_KEY) || "";
  const open = on => box.classList.toggle("open", on);
  toggle.addEventListener("click", () => open(!box.classList.contains("open")));

  const say = ({ state, room, name, peers, why }) => {
    box.dataset.state = state;
    status.textContent = {
      joining: `joining ${room} …`,
      in: `${room} · ${name}${peers?.length ? ` · with ${peers.join(", ")}` : ""}`,
      reconnecting: `${room}: lost the relay, joining again …`,
      refused: `${room}: ${why}`,
      left: "",
    }[state] ?? "";
    // an icon: the room is its title (and its colour, in the CSS: data-state)
    const label = state === "in" ? `room: ${room}` : "room";
    toggle.title = label;
    toggle.setAttribute("aria-label", label);
    if (state === "in") { open(false); onJoined(room); }
    if (state === "left") onLeft();
    if (state === "refused") { store(sessionStorage, passKey(room)); open(true); }
  };

  function join(room, password, name) {
    link?.leave();
    store(localStorage, NAME_KEY, name || undefined);
    store(sessionStorage, passKey(room), password);
    link = joinRoom({ relay, room, password, name, onStatus: say });
  }

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const room = field("room").value.trim();
    const password = field("password").value;
    if (!room || !password) { say({ state: "refused", room: room || "the room", why: "a room and its password" }); return; }
    field("password").value = "";
    join(room, password, field("name").value.trim());
  });
  leaveBtn.addEventListener("click", () => { link?.leave(); link = null; });

  return {
    join,
    kept: room => (room ? read(sessionStorage, passKey(room)) : null),
    ask(room) { field("room").value = room; open(true); field("password").focus(); },
    room: field("room").value,
    name: field("name").value,
  };
}
